import { useEffect } from 'react';
import './App.css';
import { legalDocuments } from './legalDocuments';

const PLATFORM_URL = process.env.REACT_APP_PLATFORM_URL || 'https://пантеон.com';

function LinkifiedText({ children }) {
  const parts = String(children).split(/(https:\/\/[^\s]+|help@pantheone\.ru)/gu);

  return parts.map((part, index) => {
    if (!part.startsWith('http') && part !== 'help@pantheone.ru') {
      return part;
    }

    const hasTrailingPunctuation = /[.,;:]$/u.test(part);
    const value = hasTrailingPunctuation ? part.slice(0, -1) : part;
    const suffix = hasTrailingPunctuation ? part.slice(-1) : '';
    const href = value === 'help@pantheone.ru' ? `mailto:${value}` : value;

    return (
      <span key={`${value}-${index}`}>
        <a href={href}>{value}</a>{suffix}
      </span>
    );
  });
}

function SiteHeader({ activeSlug }) {
  return (
    <header className="site-header">
      <div className="site-header__inner">
        <a className="brand" href={PLATFORM_URL} aria-label="Пантеон — главная">
          <span className="brand__name">Пантеон</span>
          <span className="brand__badge">beta</span>
        </a>

        <nav className="document-nav" aria-label="Правовые документы">
          <a className={activeSlug === 'privacy' ? 'is-active' : ''} href="/privacy" aria-current={activeSlug === 'privacy' ? 'page' : undefined}>
            Политика
          </a>
          <a className={activeSlug === 'tos' ? 'is-active' : ''} href="/tos" aria-current={activeSlug === 'tos' ? 'page' : undefined}>
            Соглашение
          </a>
        </nav>

        <a className="platform-link" href={PLATFORM_URL}>
          На платформу
        </a>
      </div>
    </header>
  );
}

function LegalTable({ rows }) {
  return (
    <div className="legal-table-wrap">
      <table className="legal-table">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td><LinkifiedText>{value}</LinkifiedText></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function sectionId(documentSlug, number) {
  return `${documentSlug}-section-${number.replaceAll('.', '-')}`;
}

function renderBlocks(document) {
  const rendered = [];

  for (let index = 0; index < document.blocks.length; index += 1) {
    const block = document.blocks[index];

    if (block.type === 'bullet') {
      const items = [];
      while (document.blocks[index]?.type === 'bullet') {
        items.push(document.blocks[index].text);
        index += 1;
      }
      index -= 1;
      rendered.push(
        <ul className="legal-list" key={`list-${index}`}>
          {items.map((item) => <li key={item}><LinkifiedText>{item}</LinkifiedText></li>)}
        </ul>,
      );
      continue;
    }

    if (block.type === 'heading') {
      rendered.push(
        <section className="legal-section-heading" id={sectionId(document.slug, block.number)} key={`heading-${block.number}`}>
          <span className="legal-section-heading__number">{block.number}</span>
          <h2>{block.text}</h2>
        </section>,
      );
      continue;
    }

    if (block.type === 'subheading') {
      rendered.push(
        <section className="legal-subheading" id={sectionId(document.slug, block.number)} key={`subheading-${block.number}`}>
          <span>{block.number}</span>
          <h3>{block.text}</h3>
        </section>,
      );
      continue;
    }

    if (block.type === 'table') {
      rendered.push(<LegalTable rows={block.rows} key={`table-${index}`} />);
      continue;
    }

    if (block.type === 'definition') {
      rendered.push(
        <dl className="legal-definition" key={`definition-${index}`}>
          <dt>{block.term}</dt>
          <dd><LinkifiedText>{block.text}</LinkifiedText></dd>
        </dl>,
      );
      continue;
    }

    rendered.push(
      <p className={`legal-paragraph${block.depth ? ' legal-paragraph--nested' : ''}`} key={`paragraph-${index}`}>
        {block.number && <span className="legal-paragraph__number">{block.number}</span>}
        <LinkifiedText>{block.text}</LinkifiedText>
      </p>,
    );
  }

  return rendered;
}

function LegalPage({ document: legalDocument }) {
  const sections = legalDocument.blocks.filter((block) => block.type === 'heading');

  useEffect(() => {
    window.document.title = `${legalDocument.title} — Пантеон`;
  }, [legalDocument.title]);

  return (
    <div className="App" id="top">
      <SiteHeader activeSlug={legalDocument.slug} />
      <main className="page-shell">
        <header className="document-hero">
          <p className="document-hero__eyebrow">{legalDocument.eyebrow}</p>
          <h1>{legalDocument.title}</h1>
          <div className="document-hero__meta">
            <span>Пантеон</span>
            {legalDocument.updated && <span>Обновлено {legalDocument.updated}</span>}
          </div>
        </header>

        <div className="document-layout">
          <aside className="table-of-contents" aria-label="Содержание документа">
            <p className="table-of-contents__title">Содержание</p>
            <ol>
              {sections.map((section) => (
                <li key={section.number}>
                    <a href={`#${sectionId(legalDocument.slug, section.number)}`}>
                    <span>{section.number}</span>{section.text}
                  </a>
                </li>
              ))}
            </ol>
            <a className="back-to-top" href="#top">К началу ↑</a>
          </aside>

          <article className="legal-document">
            <div className="legal-document__notice">
              Актуальная редакция документа для сервиса <a href={PLATFORM_URL}>пантеон.com</a>
            </div>
            <div className="legal-content">{renderBlocks(legalDocument)}</div>
          </article>
        </div>
      </main>

      <footer className="site-footer">
        <span>© {new Date().getFullYear()} Пантеон</span>
        <a href="mailto:help@pantheone.ru">help@pantheone.ru</a>
      </footer>
    </div>
  );
}

function NotFound() {
  return (
    <div className="App">
      <SiteHeader />
      <main className="not-found">
        <span>404</span>
        <h1>Документ не найден</h1>
        <p>Перейдите к политике обработки данных или пользовательскому соглашению.</p>
        <a href="/privacy">Открыть документы</a>
      </main>
    </div>
  );
}

function App() {
  const path = window.location.pathname.replace(/\/+$/u, '') || '/';
  const slug = path === '/' ? 'privacy' : path.slice(1);
  const document = legalDocuments[slug];

  return document ? <LegalPage document={document} /> : <NotFound />;
}

export default App;
